import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api, { fmtJMD, formatApiError } from "../../lib/api";
import ImageUpload, { resolveImageUrl } from "../../components/ImageUpload";

const BLANK = { name: "", description: "", price: 5400, image_url: "", flavour_names: [], active: true };

export default function AdminBoxes() {
  const [boxes, setBoxes] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => setBoxes((await api.get("/preset-boxes?active_only=false")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing, price: Number(editing.price),
                        flavour_names: typeof editing.flavour_names === "string"
                          ? editing.flavour_names.split(",").map((s) => s.trim()).filter(Boolean)
                          : editing.flavour_names };
      if (editing.id) await api.put(`/preset-boxes/${editing.id}`, payload);
      else await api.post("/preset-boxes", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this box?")) return;
    await api.delete(`/preset-boxes/${id}`); load();
  };

  return (
    <div data-testid="admin-boxes">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">Pick Ah Box</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Curated boxes</h1>
        </div>
        <button onClick={() => setEditing({ ...BLANK })} className="btn-primary inline-flex items-center gap-2" data-testid="new-box-btn">
          <Plus size={16} /> New box
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {boxes.map((b) => (
          <div key={b.id} className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid={`box-card-${b.id}`}>
            {b.image_url && <img src={b.image_url} alt={b.name} className="w-full h-40 object-cover" />}
            <div className="p-5">
              <div className="font-display font-bold">{b.name}</div>
              <div className="text-xs text-zinc-500 mt-1">{b.flavour_names.join(", ")}</div>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-semibold">{fmtJMD(b.price)}</span>
                <div className="flex gap-1">
                  <button onClick={() => setEditing({ ...b, flavour_names: b.flavour_names.join(", ") })} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`edit-box-${b.id}`}><Pencil size={14} /></button>
                  <button onClick={() => remove(b.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 sm:p-6" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-xl w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg sm:text-xl">{editing.id ? "Edit box" : "New box"}</div>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input className="input" placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-box-name" />
              <textarea className="input" placeholder="Description" rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              <div>
                <label className="text-xs font-semibold text-zinc-600 block mb-1">Box image</label>
                <ImageUpload value={editing.image_url || ""} onChange={(url) => setEditing({ ...editing, image_url: url })} testid="edit-box-image" />
              </div>
              <input className="input" placeholder="Price" type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
              <textarea className="input" placeholder="Flavour names (comma-separated)" rows={2} value={editing.flavour_names} onChange={(e) => setEditing({ ...editing, flavour_names: e.target.value })} data-testid="edit-box-flavours" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="save-box-btn">Save</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.input { width:100%; border:1px solid #e4e4e7; border-radius:0.5rem; padding:0.5rem 0.75rem; }`}</style>
    </div>
  );
}
